<template>
  <iModal
    :show-secondary-action-button="true"
    :max-width="550"
    :title="modalSubmitButton.modalTitle"
    show-footer
    :primary-action-button-label="modalSubmitButton.title"
    :primary-action-button-loading="isBusy"
    :primary-action-button-disabled="isSubmitDisabled"
    disable-close-button
    @close="closeModal"
    @click:primary="onPrimaryAction"
  >
    <iRow gap="small">
      <iRadioBar
        v-model="publishState"
        :options="publishOptions"
      />

      <template v-if="isStatePublishNow">
        <iRow
          :style-overrides="{
            marginTop: 'standard',
            marginBottom: 'standard',
          }"
        >
          <iText
            variant="subtle"
            font-size="standard"
            font-weight="light"
          >
            {{ getDescriptions.mainDescription }}
          </iText>
        </iRow>
      </template>

      <template v-else-if="isStatePublishLater">
        <iRow
          width="fill"
          align="left"
          vertical-align="top"
          :style-overrides="{
            marginTop: 'standard',
            marginBottom: 'standard',
          }"
        >
          <iText
            variant="subtle"
            font-size="standard"
            font-weight="light"
          >
            {{ getDescriptions.mainDescription }}
          </iText>
        </iRow>
        <iRow
          align="center"
          vertical-align="bottom"
          wrap="nowrap"
          :style-overrides="{
            marginBottom: 'large',
          }"
        >
          <iColumn
            align="center"
            vertical-align="middle"
            width="fill"
            :style-overrides="{
              marginLeft: 'extraLarge',
              marginRight: 'standard',
            }"
          >
            <iDatePicker
              v-model="localSelectedDate"
              width="fill"
              size="small"
              label="Date"
            />
          </iColumn>
          <iColumn
            align="center"
            vertical-align="middle"
            width="hug"
            :style-overrides="{
              marginLeft: 'standard',
              marginRight: 'extraLarge',
            }"
          >
            <iTimePicker
              v-model="localSelectedTime"
              size="standard"
              format="24"
              label="Time"
              :style-overrides="{
                inputBorderWidth: 'standard',
                inputBorderColor: 'dark',
                inputPaddingTop: 'extraSmall',
                inputPaddingBottom: 'extraSmall',
                inputBoxShadowWidth: 'none',
                inputBoxShadowColor: 'none',
              }"
            />
          </iColumn>
        </iRow>
        <iRow
          v-if="scheduleHint"
          width="fill"
          align="center"
          :style-overrides="{marginBottom: 'standard'}"
        >
          <iText variant="error" font-size="small" data-test="story-publish-schedule-hint">
            {{ scheduleHint }}
          </iText>
        </iRow>
      </template>
    </iRow>
  </iModal>
</template>

<script>
import { mapState } from "pinia";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import {
  STORY_MODAL_STATE_PUBLISH_NOW,
  STORY_MODAL_STATE_PUBLISH_LATER,
  STORY_MODAL_LABEL_PUBLISH_NOW,
  STORY_MODAL_LABEL_PUBLISH_LATER,
  STORY_MODAL_TOOLTIP_PUBLISH_NOW,
  STORY_MODAL_TOOLTIP_PUBLISH_LATER,
  STORY_MODAL_DESCRIPTION_PUBLISH_NOW,
  STORY_MODAL_DESCRIPTION_SUBMIT_FOR_REVIEW,
  STORY_MODAL_DESCRIPTION_SCHEDULE,
  STORY_MODAL_DESCRIPTION_SCHEDULE_NEEDS_REVIEW,
  STORY_MODAL_PRIMARY_BUTTON_LABEL_PUBLISH_NOW,
  STORY_MODAL_PRIMARY_BUTTON_LABEL_PUBLISH_LATER,
  STORY_MODAL_PRIMARY_BUTTON_LABEL_SUBMIT_FOR_REVIEW,
  STORY_MODAL_PRIMARY_BUTTON_LABEL_REQUEST_SCHEDULING,
} from "%/constants/story-constants";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const MSG_SCHEDULE_INCOMPLETE = "Pick a date and a time to schedule this story.";
const MSG_SCHEDULE_IN_PAST = "Pick a date and time in the future.";

export default {
  name: "StoryPublishModal",
  props: {
    selectedDate: {
      type: String,
      default: "",
      required: false,
    },
    selectedTime: {
      type: String,
      default: "",
      required: false,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    lockedMode: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "publish-now", "schedule-publish", "submit-review"],
  data() {
    return {
      localSelectedDate: null,
      localSelectedTime: null,
      publishState: this.lockedMode || STORY_MODAL_STATE_PUBLISH_NOW,
      hasSubmitted: false,
    };
  },
  computed: {
    ...mapState(useCollaboratorStore, [
      "role",
      "canPublishStories",
    ]),
    isValidRole() {
      return this.canPublishStories;
    },
    isBusy() {
      return this.isSubmitting || this.hasSubmitted;
    },
    isScheduleIncomplete() {
      return this.isStatePublishLater && !this.isScheduleValid;
    },
    scheduleHint() {
      if (!this.isScheduleIncomplete) {
        return "";
      }
      return this.composeScheduledDateTime()
        ? MSG_SCHEDULE_IN_PAST
        : MSG_SCHEDULE_INCOMPLETE;
    },
    isSubmitDisabled() {
      return this.isBusy || this.isScheduleIncomplete;
    },
    publishOptions() {
      return [
        {
          value: STORY_MODAL_STATE_PUBLISH_NOW,
          label: STORY_MODAL_LABEL_PUBLISH_NOW,
          tooltip: STORY_MODAL_TOOLTIP_PUBLISH_NOW,
          mainDescription: this.isValidRole
            ? STORY_MODAL_DESCRIPTION_PUBLISH_NOW
            : STORY_MODAL_DESCRIPTION_SUBMIT_FOR_REVIEW,
          disabled: this.isModeLocked(STORY_MODAL_STATE_PUBLISH_NOW),
        },
        {
          value: STORY_MODAL_STATE_PUBLISH_LATER,
          label: STORY_MODAL_LABEL_PUBLISH_LATER,
          tooltip: STORY_MODAL_TOOLTIP_PUBLISH_LATER,
          mainDescription: this.isValidRole
            ? STORY_MODAL_DESCRIPTION_SCHEDULE
            : STORY_MODAL_DESCRIPTION_SCHEDULE_NEEDS_REVIEW,
          disabled: this.isModeLocked(STORY_MODAL_STATE_PUBLISH_LATER),
        },
      ];
    },
    modalSubmitButton() {
      if (this.publishState === STORY_MODAL_STATE_PUBLISH_NOW) {
        return {
          modalTitle: STORY_MODAL_LABEL_PUBLISH_NOW,
          title: this.isValidRole
            ? STORY_MODAL_PRIMARY_BUTTON_LABEL_PUBLISH_NOW
            : STORY_MODAL_PRIMARY_BUTTON_LABEL_SUBMIT_FOR_REVIEW,
          method: this.isValidRole ? this.publishNow : this.submitForReview,
        };
      }
      return {
        modalTitle: STORY_MODAL_LABEL_PUBLISH_LATER,
        title: this.isValidRole
          ? STORY_MODAL_PRIMARY_BUTTON_LABEL_PUBLISH_LATER
          : STORY_MODAL_PRIMARY_BUTTON_LABEL_REQUEST_SCHEDULING,
        method: this.schedulePost,
      };
    },
    isValidAction() {
      if (this.isStatePublishNow) {
        return this.isValidRole;
      }

      if (this.isStatePublishLater) {
        return this.isValidRole && this.isScheduleValid;
      }

      return false;
    },
    getDescriptions() {
      const option = this.publishOptions.find(
        option => option.value === this.publishState,
      );

      return {
        mainDescription: option?.mainDescription,
      };
    },
    isStatePublishNow() {
      return this.publishState === STORY_MODAL_STATE_PUBLISH_NOW;
    },
    isStatePublishLater() {
      return this.publishState === STORY_MODAL_STATE_PUBLISH_LATER;
    },
    isScheduleValid() {
      const scheduled = this.composeScheduledDateTime();

      if (!scheduled) {
        return false;
      }

      return scheduled.isAfter(dayjs());
    },
  },
  watch: {
    isSubmitting(submitting) {
      if (!submitting) {
        this.hasSubmitted = false;
      }
    },
    publishState(state) {
      if (
        state === STORY_MODAL_STATE_PUBLISH_LATER &&
        !this.localSelectedTime
      ) {
        this.localSelectedTime = "00:00";
      }
    },
    selectedDate: {
      immediate: true,
      handler(date) {
        this.localSelectedDate = date;
      },
    },
    selectedTime: {
      immediate: true,
      handler(time) {
        this.localSelectedTime = time;
      },
    },
  },
  methods: {
    isModeLocked(mode) {
      return Boolean(this.lockedMode) && this.lockedMode !== mode;
    },
    closeModal() {
      this.$emit("close");
    },
    onPrimaryAction() {
      if (this.isBusy) {
        return;
      }

      this.hasSubmitted = true;
      this.modalSubmitButton.method();
    },
    publishNow() {
      if (!this.isValidRole) {
        return;
      }

      this.$emit("publish-now");
      this.closeModal();
    },
    composeScheduledDateTime() {
      const [hour, minute] = String(this.localSelectedTime ?? "").split(":").map(Number);

      if (!this.localSelectedDate || !Number.isInteger(hour) || !Number.isInteger(minute)) {
        return null;
      }

      let composedDateTime;

      if (typeof this.localSelectedDate === "number") {
        composedDateTime = dayjs.unix(this.localSelectedDate);
      } else if (typeof this.localSelectedDate === "string") {
        composedDateTime = dayjs(this.localSelectedDate, "YYYY-MM-DD", true);
      } else {
        return null;
      }

      if (!composedDateTime.isValid()) {
        return null;
      }

      return composedDateTime
          .hour(hour)
          .minute(minute)
          .second(0);
    },
    schedulePost() {
      if (!this.isScheduleValid) {
        return;
      }

      const scheduledDateTime = this.composeScheduledDateTime();

      if (!scheduledDateTime) {
        return;
      }

      const timestamp = scheduledDateTime.unix();

      this.$emit("schedule-publish", timestamp);
      this.closeModal();
    },
    submitForReview() {
      this.$emit("submit-review");
      this.closeModal();
    },
  },
};
</script>
